import type {
  AnswerPart,
  StreamEvent,
  TextEventData,
  ToolCallStage,
  Turn,
  TurnError,
  TurnStage
} from '../types'
import { widgetPart } from './widget'

/*
 * One chat turn as the drawer shows it, built up event by event (see `Turn` in ../types). `parts` is the answer in
 * stream order; `text` is all text parts joined; a widget's `block` is the card utils/widget.ts builds.
 */

export const newTurn = (prompt: string): Turn => ({
  localId: crypto.randomUUID(),
  prompt,
  stages: [],
  text: '',
  parts: [],
  chips: [],
  status: 'streaming',
  startedAt: Date.now()
})

/* a delta extends the text part it belongs to while that part is the last one, else it starts a new part */
const appendText = (
  parts: AnswerPart[],
  { part_id, delta }: TextEventData
): AnswerPart[] => {
  const last = parts.at(-1)
  if (last?.kind === 'text' && last.part_id === part_id) {
    return [...parts.slice(0, -1), { ...last, text: last.text + delta }]
  }
  return [...parts, { kind: 'text', part_id, text: delta }]
}

/* an error without a scope is one the client raised itself (abort, network), which ends the turn like scope `turn` */
const endsTurn = (error: TurnError): boolean =>
  !error.scope || error.scope === 'turn'

/* the error of a turn whose `done` reported a failure without an `error` event before it */
const TURN_FAILED: TurnError = {
  scope: 'turn',
  code: 'TURN_FAILED',
  message: '',
  retryable: true
}

/*
 * Applies one SSE event of merchant-intelligence-service's turn stream and returns the new turn. Pure, so the
 * contract's order rules are testable. A failed turn ends with `error {scope: turn}` and then `done {status: failed}`,
 * so `done` never turns a failure back into success.
 */
export function applyEvent(turn: Turn, streamEvent: StreamEvent): Turn {
  switch (streamEvent.event) {
    case 'status':
      return { ...turn, stages: [...turn.stages, streamEvent.data] }
    case 'tool_call_start': {
      const { data } = streamEvent
      return {
        ...turn,
        stages: [
          ...turn.stages,
          {
            stage: 'tool_call',
            call_id: data.call_id,
            tool_name: data.name,
            tool_args: data.arguments
          }
        ]
      }
    }
    case 'tool_call_result': {
      const { data } = streamEvent
      return {
        ...turn,
        stages: [
          ...turn.stages,
          {
            stage: 'tool_result',
            call_id: data.call_id,
            tool_name: data.name,
            isError: data.status === 'failed'
          }
        ]
      }
    }
    case 'text':
      return {
        ...turn,
        text: turn.text + streamEvent.data.delta,
        parts: appendText(turn.parts, streamEvent.data)
      }
    case 'widget':
      return {
        ...turn,
        parts: [...turn.parts, widgetPart(streamEvent.data, toolCalls(turn))]
      }
    case 'done': {
      const { data } = streamEvent
      const isFailed = turn.status === 'failed' || data.status === 'failed'
      return {
        ...turn,
        status: isFailed ? 'failed' : 'done',
        error: isFailed ? turn.error || TURN_FAILED : turn.error,
        chips: (data.suggested_follow_ups || []).map((prompt) => ({
          label: prompt,
          prompt
        })),
        usage: data.token_usage,
        conversationUsage: data.conversation_token_usage,
        substitute: data.substitute_safe_response === true,
        finishedAt: Date.now()
      }
    }
    case 'error': {
      const { data } = streamEvent
      /* a tool call's or a widget's error is a notice at its place in the answer; the turn goes on */
      if (!endsTurn(data)) {
        return {
          ...turn,
          parts: [...turn.parts, { kind: 'notice', error: data }]
        }
      }
      return {
        ...turn,
        status: data.code === 'aborted' ? 'aborted' : 'failed',
        error: data,
        finishedAt: Date.now()
      }
    }
    default:
      return turn
  }
}

const isToolCall = (stage: TurnStage): stage is ToolCallStage =>
  stage.stage === 'tool_call'

/* the tool calls of a turn, for the "answered with N tool calls" summary */
export const toolCalls = (turn: Turn): ToolCallStage[] =>
  turn.stages.filter(isToolCall)
